/**
 * TaskPausesTab - Aba de histórico de pausas no modal de tarefa
 * 
 * Exibe lista cronológica de pausas com status, duração, responsáveis e motivo.
 * 
 * @version 1.0.0
 * RELEASE: 2026-04-11 v1.0.0 - Criação inicial
 */

import { Pause, Play, Clock, User } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useTaskPauses } from '@/hooks/useTaskPauses';
import { formatPauseDuration } from '@/lib/utils/pause-helpers';
import type { TaskDetail } from '@/hooks/useTaskDetails';

/**
 * Props da aba de pausas
 */
interface TaskPausesTabProps {
  task: TaskDetail;
}

/**
 * Aba de histórico de pausas
 */
export function TaskPausesTab({ task }: TaskPausesTabProps) {
  const { pauses, totalCount, totalDurationSeconds, isLoading } = useTaskPauses(task.id);

  /** Loading state */
  if (isLoading) {
    return (
      <div className="space-y-3 p-1">
        {[1, 2, 3].map(i => (
          <Skeleton key={i} className="h-24 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  /** Estado vazio */
  if (pauses.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48 gap-3">
        <Pause className="h-10 w-10 text-muted-foreground/30" />
        <div className="text-center">
          <p className="text-sm font-medium text-muted-foreground">Nenhuma pausa registrada</p>
          <p className="text-xs text-muted-foreground/60 mt-1">
            As pausas serão exibidas aqui quando a tarefa for pausada.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 p-1">
      {/* Resumo no topo */}
      <div className="flex items-center justify-between px-1">
        <span className="text-xs text-muted-foreground">
          {totalCount} {totalCount === 1 ? 'pausa' : 'pausas'} registradas
        </span>
        <span className="text-xs font-medium text-foreground flex items-center gap-1">
          <Clock className="h-3 w-3" />
          Tempo total: {formatPauseDuration(totalDurationSeconds)}
        </span>
      </div>

      {/* Lista de pausas */}
      {pauses.map((pause) => {
        const isActive = !pause.resumed_at;

        // Padrão visual unificado (InlineNotice):
        // sem fundo, barra lateral colorida (âmbar = em pausa, esmeralda = concluída)
        return (
          <div
            key={pause.id}
            className={cn(
              'flex gap-2 border-l-4 bg-transparent px-3 py-2 space-y-2',
              isActive
                ? 'border-l-amber-500 dark:border-l-amber-400'
                : 'border-l-emerald-500 dark:border-l-emerald-400'
            )}
          >
            <div className="flex-1 space-y-2">
              {/* Header: status + duração */}
              <div className="flex items-center justify-between">
                <span
                  className={cn(
                    'inline-flex items-center gap-1 text-xs font-medium',
                    isActive
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-emerald-600 dark:text-emerald-400'
                  )}
                >
                  {isActive ? (
                    <><Pause className="h-3 w-3" /> Em Pausa</>
                  ) : (
                    <><Play className="h-3 w-3" /> Pausa Concluída</>
                  )}
                </span>

                <span className="text-xs font-mono text-muted-foreground">
                  {formatPauseDuration(pause.duration_seconds)}
                </span>
              </div>

              {/* Quem pausou + data */}
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <User className="h-3 w-3" />
                <span className="font-medium text-foreground">
                  {pause.paused_by_name || 'Usuário'}
                </span>
                <span>pausou em</span>
                <span>{format(new Date(pause.paused_at), "dd/MM/yy 'às' HH:mm", { locale: ptBR })}</span>
              </div>

              {/* Motivo (se informado) */}
              {pause.reason && (
                <div className="text-xs text-foreground">
                  {pause.reason}
                </div>
              )}

              {/* Quem retomou + data (se retomada) */}
              {pause.resumed_at && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <User className="h-3 w-3" />
                  <span className="font-medium text-foreground">
                    {pause.resumed_by_name || 'Usuário'}
                  </span>
                  <span>retomou em</span>
                  <span>{format(new Date(pause.resumed_at), "dd/MM/yy 'às' HH:mm", { locale: ptBR })}</span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
